import React, { useState, useEffect, useMemo } from 'react';
import { getTeamLogo, normalizeTeamName } from '../utils/teamUtils';

const TeamStats = ({ players, isDarkMode }) => {
  const [teamStats, setTeamStats] = useState([]);
  const [year, setYear] = useState('2025');
  const [loading, setLoading] = useState(true);
  const [selectedGameSpan, setSelectedGameSpan] = useState('All Games');
  const [selectedPerspective, setSelectedPerspective] = useState('For');
  const [debugInfo, setDebugInfo] = useState(null);
  const [expandedCell, setExpandedCell] = useState(null); // {team, stat}
  const [rawGamesData, setRawGamesData] = useState({});
  const [sortColumn, setSortColumn] = useState('team');  // Default sort by team name
  const [sortDirection, setSortDirection] = useState('asc'); // Default to alphabetical A-Z

  // Filter options
  const years = ['2025','2024', '2023', '2022', '2021', '2020'];
  const gameSpanOptions = [
    { id: 'L3', label: 'Last 3' },
    { id: 'L5', label: 'Last 5' },
    { id: 'L10', label: 'Last 10' },
    { id: 'All', label: 'All Games' }
  ];
  const perspectiveOptions = ['For', 'Against'];

  // Define the statistics to display in the table
  const displayStats = [
    { key: 'points', label: 'POINTS' },
    { key: 'tries', label: 'TRIES' },
    { key: 'conversions', label: 'CONVERSIONS' },
    { key: 'penalty_goals', label: 'PENALTY GOALS' },
    { key: 'all_run_metres', label: 'RUN METERS' },
    { key: 'post_contact_metres', label: 'POST CONTACT M' },
    { key: 'line_breaks', label: 'LINE BREAKS' },
    { key: 'line_break_assists', label: 'LINE BREAK ASSISTS' },
    { key: 'try_assists', label: 'TRY ASSISTS' },
    { key: 'tackle_breaks', label: 'TACKLE BREAKS' },
    { key: 'offloads', label: 'OFFLOADS' },
    { key: 'receipts', label: 'RECEIPTS' },
    { key: 'tackles_made', label: 'TACKLES MADE' },
    { key: 'missed_tackles', label: 'MISSED TACKLES' },
    { key: 'ineffective_tackles', label: 'INEFFECTIVE TACKLES' },
    { key: 'intercepts', label: 'INTERCEPTS' },
    { key: 'kicks', label: 'KICKS' },
    { key: 'forced_drop_outs', label: 'FORCED DROP OUTS' },
    { key: 'errors', label: 'ERRORS', inverse: true },
    { key: 'penalties', label: 'PENALTIES', inverse: true },
    { key: 'performance_points', label: 'PP' }
  ];

  // Add this helper function at the top of the component
  const extractRoundNumber = (roundString) => {
    const match = roundString.match(/\d+/);
    return match ? parseInt(match[0]) : 0;
  };

  useEffect(() => {
    if (players && players.length > 0) {
      calculateTeamStats();
    }
  }, [players, year, selectedGameSpan, selectedPerspective]);

  const calculateTeamStats = () => {
    setLoading(true);
    
    try {
      const filteredByYear = players.filter(player => String(player.year) === String(year));
      const teams = [...new Set(filteredByYear.map(player => player.player_team))];
      const teamStatsData = [];
      const rawData = {};

      teams.forEach(team => {
        const teamPlayers = filteredByYear.filter(player => {
          // For perspective: get stats where this is the player's team
          // Against perspective: get stats where this team is the opponent
          if (selectedPerspective === 'For') {
            return player.player_team === team;
          } else {
            // For 'Against', we want stats from the opposing team when playing against this team
            return (player.home_team === team && player.player_team === player.away_team) ||
                   (player.away_team === team && player.player_team === player.home_team);
          }
        });
        rawData[team] = {};
        
        // Get unique matches and sort by round number (most recent first)
        const uniqueMatches = [...new Set(teamPlayers.map(player => 
          `${player.year}_${player.round}_${player.player_team}_${player.home_team}_${player.away_team}`
        ))].sort((a, b) => {
          const roundA = extractRoundNumber(a.split('_')[1]);
          const roundB = extractRoundNumber(b.split('_')[1]);
          return roundB - roundA;
        });

        // Apply game span filter
        let filteredMatches = uniqueMatches;
        if (selectedGameSpan === 'Last 3') {
          filteredMatches = uniqueMatches.slice(0, 3);
        } else if (selectedGameSpan === 'Last 5') {
          filteredMatches = uniqueMatches.slice(0, 5);
        } else if (selectedGameSpan === 'Last 10') {
          filteredMatches = uniqueMatches.slice(0, 10);
        }

        // Process each filtered match to get team-level stats
        const teamGames = filteredMatches.map(matchKey => {
          const [year, round, playerTeam, homeTeam, awayTeam] = matchKey.split('_');
          const matchPlayers = teamPlayers.filter(p => 
            p.round === round && 
            p.player_team === playerTeam && 
            p.home_team === homeTeam &&
            p.away_team === awayTeam
          );

          // Calculate team totals for this match
          const gameStats = {};
          displayStats.forEach(stat => {
            if (stat.key === 'performance_points') {
              let totalPP = 0;
              matchPlayers.forEach(player => {
                totalPP += (parseFloat(player['points']) || 0) * 4;
                totalPP += (parseFloat(player['try_assists']) || 0) * 10;
                totalPP += Math.floor((parseFloat(player['all_run_metres']) || 0) / 10);
                totalPP += (parseFloat(player['line_breaks']) || 0) * 5;
                totalPP += (parseFloat(player['tackles_made']) || 0);
              });
              gameStats[stat.key] = totalPP;
            } else {
              gameStats[stat.key] = matchPlayers.reduce((sum, player) => {
                return sum + (parseFloat(player[stat.key]) || 0);
              }, 0);
            }
          });

          // Add match metadata with corrected opponent logic
          const opponent = selectedPerspective === 'For' 
            ? (team === homeTeam ? awayTeam : homeTeam)  // For: show who they played against
            : playerTeam;  // Against: show the team that accumulated these stats against 'team'

          return {
            round,
            opponent,
            venue: matchPlayers[0]?.venue || '',
            weather: matchPlayers[0]?.weather || '',
            result: matchPlayers[0]?.result || '',
            ...gameStats
          };
        });

        // Store raw game data for each stat
        displayStats.forEach(stat => {
          rawData[team][stat.key] = teamGames;
        });

        // Calculate averages based on filtered games
        const result = { team, matchCount: teamGames.length };
        displayStats.forEach(stat => {
          if (teamGames.length > 0) {
            const total = teamGames.reduce((sum, game) => {
              return sum + (parseFloat(game[stat.key]) || 0);
            }, 0);
            result[stat.key] = parseFloat((total / teamGames.length).toFixed(1));
          } else {
            result[stat.key] = 0;
          }
        });

        teamStatsData.push(result);
      });

      setTeamStats(teamStatsData);
      setRawGamesData(rawData);
      
      // Save debug info
      setDebugInfo({
        totalPlayers: players.length,
        filteredPlayers: filteredByYear.length,
        teams: teams.length,
        calculatedTeams: teamStatsData.length
      });
    } catch (error) {
      console.error("Error calculating team stats:", error);
      setDebugInfo({ error: error.message });
    } finally {
      setLoading(false);
    }
  };

  // Helper function to calculate average
  const calculateAverage = (games, statKey, matchCount) => {
    const sum = games.reduce((total, game) => {
      const value = game[statKey];
      let numValue = 0;
      if (value !== null && value !== undefined && value !== "-") {
        numValue = parseFloat(value) || 0;
      }
      return total + numValue;
    }, 0);
    
    return sum / matchCount;
  };
  
  // Define color tiers for the bubbles
  const getStatColorClass = (values, value) => {
    if (!values.length) return 'bg-gray-200 dark:bg-gray-700';
    
    const sortedValues = [...values].sort((a, b) => b - a);
    const index = sortedValues.indexOf(value);
    const percentile = index / sortedValues.length;
    
    if (percentile <= 0.2) {
      return 'bg-green-500 text-white';
    } else if (percentile <= 0.4) {
      return 'bg-lime-400 text-black';
    } else if (percentile <= 0.6) {
      return 'bg-yellow-400 text-black';
    } else if (percentile <= 0.8) {
      return 'bg-orange-400 text-black';
    } else {
      return 'bg-red-400 text-white';
    }
  };

  // For inverse stats like errors/penalties where lower is better
  const getInverseStatColorClass = (values, value) => {
    if (!values.length) return 'bg-gray-200 dark:bg-gray-700';
    
    const sortedValues = [...values].sort((a, b) => a - b);
    const index = sortedValues.indexOf(value);
    const percentile = index / sortedValues.length;
    
    if (percentile <= 0.2) {
      return 'bg-green-500 text-white';
    } else if (percentile <= 0.4) {
      return 'bg-lime-400 text-black';
    } else if (percentile <= 0.6) {
      return 'bg-yellow-400 text-black';
    } else if (percentile <= 0.8) {
      return 'bg-orange-400 text-black';
    } else {
      return 'bg-red-400 text-white';
    }
  };

  // Get all values for a specific stat to determine rankings
  const getStatValues = (statKey) => {
    return teamStats.map(team => team[statKey]);
  };

  // Replace old getColorClass with this new function
  const getBubbleClass = (values, value, inverse = false) => {
    return inverse 
      ? getInverseStatColorClass(values, value)
      : getStatColorClass(values, value);
  };

  const handleCellClick = (team, stat) => {
    if (expandedCell && expandedCell.team === team && expandedCell.stat === stat) {
      setExpandedCell(null);
    } else {
      const games = rawGamesData[team]?.[stat] || [];
      if (games.length > 0) {
        setExpandedCell({ team, stat });
      }
    }
  };

  const isCellExpanded = (team, stat) => {
    return expandedCell && expandedCell.team === team && expandedCell.stat === stat;
  };

  // Modify the handleSort function to handle team column
  const handleSort = (statKey) => {
    // If statKey is null, we're sorting by team name
    const column = statKey || 'team';
    
    // If clicking the same column, toggle direction
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // If clicking a new column, set it and default to appropriate direction
      setSortColumn(column);
      // Default to alphabetical order for team names, descending for stats
      setSortDirection(column === 'team' ? 'asc' : 'desc');
    }
  };

  // Update the sortedTeamStats function to handle team name sorting
  const sortedTeamStats = useMemo(() => {
    if (!teamStats.length) return [];
    
    return [...teamStats].sort((a, b) => {
      // Special case for team name sorting
      if (sortColumn === 'team') {
        return sortDirection === 'asc' 
          ? a.team.localeCompare(b.team) 
          : b.team.localeCompare(a.team);
      }
      
      const valueA = a[sortColumn] || 0;
      const valueB = b[sortColumn] || 0;
      
      // Sort based on direction
      return sortDirection === 'asc' 
        ? valueA - valueB 
        : valueB - valueA;
    });
  }, [teamStats, sortColumn, sortDirection]);

  return (
    <div className="w-full px-4 py-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg h-full overflow-auto">
        {/* Header with more concise layout for desktop */}
        <div className="p-2 md:p-3">
          <h1 className="text-lg font-bold text-gray-800 dark:text-white mb-2">Team Statistics</h1>
          
          {/* Filters - single row layout for desktop */}
          <div className="flex flex-wrap items-center gap-1 md:gap-2">
            {/* Year selection - unified style */}
            <div className="flex flex-wrap gap-1 md:gap-1.5">
              {years.map(y => (
                <button
                  key={y}
                  className={`px-1.5 py-0.5 md:px-2.5 md:py-1 text-xs md:text-xs rounded-md transition-colors duration-150 ${
                    year === y 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                  }`}
                  onClick={() => setYear(y)}
                >
                  {y}
                </button>
              ))}
            </div>
            
            {/* Game span filters with better spacing */}
            <div className="flex flex-wrap gap-1 md:gap-1.5 ml-0 md:ml-3">
              {gameSpanOptions.map(option => (
                <button
                  key={option.id}
                  className={`px-1.5 py-0.5 md:px-2.5 md:py-1 text-xs md:text-xs rounded-md transition-colors duration-150 ${
                    selectedGameSpan === option.label
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                  }`}
                  onClick={() => setSelectedGameSpan(option.label)}
                >
                  {option.label}
                </button>
              ))}
            </div>
            
            {/* Perspective filters aligned */}
            <div className="flex flex-wrap gap-1 md:gap-1.5 ml-0 md:ml-3">
              {perspectiveOptions.map(option => (
                <button
                  key={option}
                  className={`px-1.5 py-0.5 md:px-2.5 md:py-1 text-xs md:text-xs rounded-md transition-colors duration-150 ${
                    selectedPerspective === option 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                  }`}
                  onClick={() => setSelectedPerspective(option)}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
        </div>
        
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : teamStats.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-[10px] md:text-xs">
              <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-20">
                <tr>
                  <th 
                    className="px-1 py-1.5 md:px-2 md:py-2 text-left text-[10px] md:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider sticky left-0 bg-gray-50 dark:bg-gray-800 z-30 w-[80px] md:w-[120px] cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={() => handleSort('team')}
                  >
                    <div className="flex items-center">
                      TEAM
                      {sortColumn === 'team' && (
                        <span className="ml-1">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  {/* Show more columns on desktop - adjust the slice based on screen width */}
                  {displayStats.slice(0, window.innerWidth < 768 ? 6 : undefined).map(stat => (
                    <th 
                      key={stat.key} 
                      className="px-1 py-1.5 md:px-2 md:py-2 text-center text-[10px] md:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={() => handleSort(stat.key)}
                    >
                      <div className="flex items-center justify-center">
                        {stat.label}
                        {sortColumn === stat.key && (
                          <span className="ml-1">
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {sortedTeamStats
                  .map((teamStat, index) => (
                    <React.Fragment key={teamStat.team}>
                      <tr className={index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-900/50'}>
                        <td className="px-1 py-1.5 md:px-2 md:py-2 whitespace-nowrap text-[10px] md:text-xs font-medium text-gray-900 dark:text-white sticky left-0 bg-inherit z-10 w-[80px] md:w-[120px]">
                          <div className="flex items-center">
                            <img 
                              src={getTeamLogo(teamStat.team)} 
                              alt={teamStat.team} 
                              className="w-4 h-4 md:w-5 md:h-5 mr-1"
                              onError={(e) => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/16?text=NRL'; }}
                            />
                            <span className="truncate max-w-[60px] md:max-w-[90px]">{normalizeTeamName(teamStat.team)}</span>
                          </div>
                        </td>
                        {/* Show all stats on desktop */}
                        {displayStats.slice(0, window.innerWidth < 768 ? 6 : undefined).map(stat => {
                          const value = teamStat[stat.key];
                          const isActive = isCellExpanded(teamStat.team, stat.key);
                          const hasData = rawGamesData[teamStat.team]?.[stat.key]?.length > 0;

                          return (
                            <td 
                              key={stat.key} 
                              onClick={() => hasData && handleCellClick(teamStat.team, stat.key)}
                              className={`px-1 py-1 md:px-2 md:py-1.5 whitespace-nowrap text-center ${hasData ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-750' : ''}`}
                            >
                              <div className={`rounded-full inline-block py-0.5 px-1.5 md:py-0.5 md:px-2 min-w-[36px] md:min-w-[44px] text-[10px] md:text-xs font-medium
                                ${getBubbleClass(getStatValues(stat.key), value, stat.inverse)}
                                ${isActive ? 'ring-1 ring-blue-500' : ''}`}
                              >
                                {value}
                              </div>
                            </td>
                          );
                        })}
                      </tr>

                      {/* Expanded row with improved layout */}
                      {expandedCell?.team === teamStat.team && (
                        <tr>
                          <td colSpan={displayStats.length + 1} className="p-0">
                            <div className="p-2 md:p-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                              <div className="flex justify-between items-center mb-1 md:mb-2">
                                <h3 className="text-[10px] md:text-xs font-medium text-gray-900 dark:text-white flex items-center">
                                  <img 
                                    src={getTeamLogo(teamStat.team)} 
                                    alt={teamStat.team} 
                                    className="w-4 h-4 md:w-5 md:h-5 mr-1"
                                  /> 
                                  {displayStats.find(s => s.key === expandedCell.stat)?.label} Game Log
                                </h3>
                                <button 
                                  onClick={() => setExpandedCell(null)}
                                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
                                >
                                  <svg className="h-3 w-3 md:h-4 md:w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </div>

                              <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-[10px] md:text-xs">
                                  <thead className="bg-gray-50 dark:bg-gray-800">
                                    <tr>
                                      <th className="px-1 py-1 md:px-2 md:py-1.5 text-left text-[10px] md:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Round
                                      </th>
                                      <th className="px-1 py-1 md:px-2 md:py-1.5 text-left text-[10px] md:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Opponent
                                      </th>
                                      <th className="px-1 py-1 md:px-2 md:py-1.5 text-center text-[10px] md:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        {displayStats.find(s => s.key === expandedCell.stat)?.label}
                                      </th>
                                      <th className="px-1 py-1 md:px-2 md:py-1.5 text-left text-[10px] md:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider hidden md:table-cell">
                                        Venue
                                      </th>
                                      <th className="px-1 py-1 md:px-2 md:py-1.5 text-center text-[10px] md:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider hidden md:table-cell">
                                        Result
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                    {rawGamesData[teamStat.team][expandedCell.stat]
                                      .map((game, index) => (
                                        <tr key={index} className="hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-150">
                                          <td className="px-1 py-1 md:px-2 md:py-1.5 whitespace-nowrap text-[10px] md:text-xs text-gray-600 dark:text-gray-300">
                                            {game.round}
                                          </td>
                                          <td className="px-1 py-1 md:px-2 md:py-1.5 whitespace-nowrap text-[10px] md:text-xs text-gray-600 dark:text-gray-300">
                                            <div className="flex items-center">
                                              <img 
                                                src={getTeamLogo(game.opponent)} 
                                                alt={game.opponent} 
                                                className="w-3 h-3 md:w-4 md:h-4 mr-1"
                                                onError={(e) => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/12?text=NRL'; }}
                                              />
                                              {normalizeTeamName(game.opponent)}
                                            </div>
                                          </td>
                                          <td className="px-1 py-1 md:px-2 md:py-1.5 whitespace-nowrap text-[10px] md:text-xs text-center">
                                            <div className={`rounded-full inline-block py-0.5 px-1.5 md:py-0.5 md:px-2 min-w-[32px] md:min-w-[40px] font-medium ${
                                              getBubbleClass([game[expandedCell.stat]], game[expandedCell.stat])
                                            }`}>
                                              {game[expandedCell.stat]}
                                            </div>
                                          </td>
                                          <td className="px-1 py-1 md:px-2 md:py-1.5 whitespace-nowrap text-[10px] md:text-xs text-gray-600 dark:text-gray-300 hidden md:table-cell">
                                            {game.venue || '-'}
                                          </td>
                                          <td className="px-1 py-1 md:px-2 md:py-1.5 whitespace-nowrap text-[10px] md:text-xs text-center text-gray-600 dark:text-gray-300 hidden md:table-cell">
                                            {game.result || '-'}
                                          </td>
                                        </tr>
                                      ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500 dark:text-gray-400">
            <p className="text-sm mb-4">No data available for the selected filters</p>
            {debugInfo && (
              <div className="text-xs text-left p-3 bg-gray-100 dark:bg-gray-700 rounded-lg max-w-full">
                <p>Debug information:</p>
                <pre className="mt-1 whitespace-pre-wrap text-[10px]">
                  {JSON.stringify(debugInfo, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamStats;